import { TrashCan16, Upload16 } from '@carbon/icons-react';
// import { Upload } from 'antd';
import { Upload } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import { UploadFile } from 'antd/lib/upload/interface';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CustomerPortalButtonType } from 'api/customer-portal/theme-api';
import { ProjectFileGroup } from 'api/project-files/types';
import { Button } from 'components/atoms/button';
import { CpBody02Primary } from 'components/atoms/customer-portal/typography';
import { Form } from 'components/atoms/form';
import { toastService } from 'components/molecules/toast';
import { MAX_FILE_SIZE_LIMIT } from 'components/organisms/files/constants';
import { CreateGroupFormProps } from 'components/organisms/projects/files/create-group-modal';
import { FileExtensionIcon } from 'components/organisms/projects/files/file-extension-icon';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useAccount } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import { formatBytes } from 'utils/files';
import { CpButton } from '../../components/cp-button';
import { CpInput } from '../../components/cp-input';
import { CpModal } from '../../components/modal/cp-modal';

// const StyledUpload = styled(Upload)`
//   .ant-upload {
//     padding: 0;
//   }
// `;

const StyledModal = styled(CpModal)`
  @media (max-width: 480px) {
    .ant-modal-body {
      height: calc(100% - 140px);
    }
    .ant-modal-footer {
      padding-top: 0;
    }
  }
`;

const FilesFormItemWrapper = styled.div`
  height: 214px;
  overflow-y: auto;
`;

const StyledDragger = styled(Dragger)`
  text-align: left;
  padding: var(--cp-spacing-05);
`;

const DraggerTitle = styled.p`
  font-size: var(--heading-6-size);
  margin-bottom: var(--cp-spacing-05);
`;

const DraggerOption = styled.p`
  color: var(--gray-40);
  font-size: var(--font-size-sm);
  margin-bottom: var(--cp-spacing-04);
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    padding-bottom: 0;
  }
`;

const HiddenUpload = styled(Upload)`
  display: none;
`;

const UploadFileList = styled.div`
  display: flex;
  align-items: center;
  padding: var(--cp-spacing-04);
  border: 1px solid var(--gray-20);
  &:not(:first-child) {
    border-top: none;
  }
  height: 40px;

  .file-name {
    flex: 3;
    margin: 0 var(--cp-spacing-04);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .file-size {
    flex: 1;
  }
  .remove-button {
    opacity: 0;
  }
  &:hover {
    .remove-button {
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    .remove-button {
      width: auto;
    }
  }
`;

export const CPCreateFolderAndUploadFileModal = ({
  onSubmit,
  onClose,
  isCreatingGroup,
  folders,
  projectId,
}: {
  onSubmit: (form: CreateGroupFormProps) => void;
  onClose: VoidFunction;
  isCreatingGroup: boolean;
  folders?: ProjectFileGroup[];
  projectId: number;
}) => {
  const account = useAccount();
  const fileSizeLimit =
    account?.metaData?.['ATTACHMENTS-maxFileSizeInBytes'] ??
    MAX_FILE_SIZE_LIMIT;
  const translate = useTranslate();
  const [form] = Form.useForm<CreateGroupFormProps>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const folderNames = useMemo(
    () => folders?.map((_folder) => _folder.folderName.toLowerCase()) || [],
    [folders],
  );
  const beforeUpload = () => false;
  const onChange = ({ file: currentFile, fileList: _list }) => {
    /**
     * This callback gets executed for all the files present in file list,
     * whereas we need to run the validation only once,
     * hence executing on the last iteration.
     */
    if (currentFile.uid !== _list[_list.length - 1].uid) {
      return;
    }
    let largeFileCount = 0;
    let invalidTypeCount = 0;

    const validList = _list.filter((_file) => {
      if (isEmpty(_file.type)) {
        invalidTypeCount += 1;

        return false;
      }
      if (_file.size > fileSizeLimit) {
        largeFileCount += 1;

        return false;
      }

      return true;
    });

    if (largeFileCount > 0) {
      toastService({
        title: translate('project.files.upload.error.title'),
        message: translate('project.files.upload.size.limit.error.message', {
          count: largeFileCount,
        }),
      }).error();
    }

    if (invalidTypeCount > 0) {
      toastService({
        title: translate('project.files.upload.error.title'),
        message: translate('project.files.upload.unknown.type.error.message', {
          count: invalidTypeCount,
        }),
      }).error();
    }

    setFileList(validList);
  };

  useEffect(() => {
    form.setFieldsValue({
      files: fileList,
    });
  }, [fileList, form]);

  const onRemove = (uid: string) => {
    setFileList([...fileList.filter((_file) => _file.uid !== uid)]);
  };

  const _onSubmit = async () => {
    const values = await form.validateFields();

    onSubmit(values);
  };

  const UploadListItem = ({ file, remove }) => {
    return (
      <UploadFileList key={file.uid}>
        <FileExtensionIcon name={file.name} />
        <div
          className="file-name"
          data-cy={`project.files.create.uploaded_file.${file.name}`}
        >
          {file.name}
        </div>
        <div className="file-size">{formatBytes(file.size, 1)}</div>
        <Button
          className="remove-button"
          type="link"
          onClick={remove}
          size="small"
          icon={<TrashCan16 />}
          data-cy={`project.files.create.uploaded_file.${file?.name}.remove.button`}
        />
      </UploadFileList>
    );
  };

  const { hasAccessToResource } = useWithAccess();
  const projectResource = useProjectAsRBACResource(projectId);

  const hasAccessToCreatePrivateGroup = hasAccessToResource({
    actionScope: ['PRIVATE'],
    rule: 'FOLDER:CREATE',
    resourceData: projectResource,
  });

  const hasAccessToCreateSharedGroup = hasAccessToResource({
    actionScope: ['SHARED'],
    rule: 'FOLDER:CREATE',
    resourceData: projectResource,
  });

  return (
    <StyledModal
      onOk={_onSubmit}
      visible
      getContainer={() =>
        document.getElementById('cp-modal-root') as HTMLElement
      }
      onCancel={onClose}
      className="cp-create-folder-and-upload-file-modal"
      title={
        <CpBody02Primary>
          <FormattedMessage id="project.files.create.group.modal.title" />
        </CpBody02Primary>
      }
      okText={<FormattedMessage id="upload" />}
      cancelButtonProps={{
        style: { display: fileList?.length > 0 ? 'inline' : 'none' },
        onClick: () => {
          if (!fileList?.length) {
            onClose?.();
          } else {
            const hiddenUpload = document.querySelector(
              '.cp-all-files-upload-hidden',
            );

            hiddenUpload?.querySelector?.('input')?.click?.();
          }
        },
      }}
      okButtonProps={{
        loading: isCreatingGroup,
        disabled: fileList?.length <= 0,
      }}
      cancelText={
        fileList?.length ? (
          <FormattedMessage id="project.files.create.folder.footer.button.add.more" />
        ) : (
          <FormattedMessage id="cancel" />
        )
      }
    >
      <HiddenUpload
        beforeUpload={beforeUpload}
        onChange={onChange}
        showUploadList={false}
        fileList={fileList}
        multiple
        id="cp-all-files-upload-hidden"
        prefixCls="cp-all-files-upload-hidden"
      />
      <StyledForm layout="vertical" form={form} autoComplete="off">
        <Form.Item
          required
          rules={[
            { required: true, message: translate('input.required') },
            ({ isFieldTouched }) => ({
              validator: (_, value) => {
                if (!isFieldTouched('groupName')) {
                  return Promise.resolve();
                }
                if (folderNames.includes(value?.toLowerCase())) {
                  return Promise.reject(new Error(`${value} already exists`));
                }

                return Promise.resolve();
              },
            }),
          ]}
          name="groupName"
          label={
            <FormattedMessage id="project.files.create.group.name.input.label" />
          }
        >
          <CpInput data-cy="project.files.create.folder.name.input" />
        </Form.Item>

        {!hasAccessToCreatePrivateGroup && hasAccessToCreateSharedGroup ? (
          <Form.Item name="permission" initialValue="shared" />
        ) : null}

        <Form.Item
          required
          name="files"
          rules={[{ required: true, message: translate('input.required') }]}
          label={
            <FormattedMessage id="project.files.create.group.files.input.label" />
          }
        >
          <FilesFormItemWrapper>
            {fileList.length <= 0 && (
              <StyledDragger
                beforeUpload={beforeUpload}
                onChange={onChange}
                showUploadList={false}
                fileList={fileList}
                multiple
                data-cy="project.files.upload.input"
              >
                <DraggerTitle>
                  <FormattedMessage id="project.files.dragger.title" />
                </DraggerTitle>
                <DraggerOption>
                  <FormattedMessage id="project.files.dragger.optional.text" />
                </DraggerOption>
                <CpButton
                  cpType={CustomerPortalButtonType.Tertiary}
                  icon={<Upload16 />}
                  iconPlacement="right"
                  data-cy="project.files.create.folder.upload.button"
                >
                  <FormattedMessage id="project.files.dragger.select.button" />
                </CpButton>
              </StyledDragger>
            )}

            {fileList.map((_file: UploadFile) => (
              <UploadListItem
                key={_file.uid}
                file={_file}
                remove={() => onRemove(_file.uid)}
              />
            ))}
          </FilesFormItemWrapper>
        </Form.Item>
      </StyledForm>
    </StyledModal>
  );
};
